import type { CommerceAdminSectionId } from '@workspace/silk-ui';

export type CommerceHelpArticleSection = {
  id: string;
  title: string;
  paragraphs: string[];
};

export type CommerceHelpArticle = {
  sectionId: CommerceAdminSectionId;
  title: string;
  summary: string;
  metrics: Array<{ label: string; value: string }>;
  sections: CommerceHelpArticleSection[];
  relatedSections: CommerceAdminSectionId[];
};

const articles: Record<CommerceAdminSectionId, CommerceHelpArticle> = {
  storefronts: {
    sectionId: 'storefronts',
    title: 'Storefronts',
    summary:
      'Storefronts are the operator-first runtime setup surface. They combine publication, channel, caller, and binding wiring into one guided workflow.',
    metrics: [
      { label: 'Object', value: 'Runtime experience' },
      { label: 'Controls', value: 'Publication, channel, principals, binding' },
      { label: 'Used By', value: 'bff-acd public and business signup' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What This Is',
        paragraphs: [
          'A storefront experience is the safe runtime bundle for one public commerce flow. It ties together the catalog release, storefront channel, allowed callers, and primary binding that the public BFF needs to resolve deterministically.',
          'Use Storefronts when you need a tenant catalog to become publicly runnable without jumping between separate publication and channel setup pages.',
        ],
      },
      {
        id: 'how',
        title: 'How It Works',
        paragraphs: [
          'The combined storefront wizard can create or attach the publication, create or attach the channel, add default callers like bff-acd, and repair missing bindings from one place.',
          'Readiness checks explain exactly what is broken so operators do not need traces or direct database inspection to diagnose runtime setup failures.',
        ],
      },
      {
        id: 'mistakes',
        title: 'Common Mistakes',
        paragraphs: [
          'Do not treat Storefronts as the place to edit products. Product structure and merchandising still belong in Catalogs.',
          'Do not hand-copy publication and channel UUIDs into public runtime config when an experience key is available. That recreates the brittle setup this page is meant to replace.',
        ],
      },
    ],
    relatedSections: ['catalogs', 'publications', 'channels'],
  },
  catalogs: {
    sectionId: 'catalogs',
    title: 'Catalogs',
    summary:
      'Catalogs are the owner-controlled product library. They define the canonical commercial objects that every other commerce surface builds on.',
    metrics: [
      { label: 'Object', value: 'Product library' },
      { label: 'Controls', value: 'Products and variants' },
      { label: 'Used By', value: 'Publications, pricebooks, orders' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What This Is',
        paragraphs: [
          'A catalog is the top-level commercial container for what you sell. It owns products, variants, descriptive metadata, and the canonical identifiers that downstream pricing and sales workflows depend on.',
          'Use catalogs when you need one tenant to curate the official product model that other selling contexts can publish, price, and transact against.',
        ],
      },
      {
        id: 'when',
        title: 'When To Use It',
        paragraphs: [
          'Use a catalog when the owner tenant is defining the product structure itself: what products exist, which variants are sellable, and which commercial identifiers should remain stable over time.',
          'Do not use catalogs to manage reseller-specific pricing or payout policy. Those belong in pricebooks and commission plans.',
        ],
      },
      {
        id: 'how',
        title: 'How It Works',
        paragraphs: [
          'Products and variants are canonical records. Publications decide where they are exposed. Pricebooks decide what a buyer sees commercially. Commission plans decide how a seller gets paid.',
          'Because catalogs are upstream of those other objects, changing them has broad impact. Treat catalog changes as product-model changes, not temporary sales configuration.',
        ],
      },
      {
        id: 'mistakes',
        title: 'Common Mistakes',
        paragraphs: [
          'Do not clone products just to express a different price. That creates catalog drift and makes reporting harder.',
          'Do not use variant metadata as a substitute for actual product structure when the difference changes what can be sold or fulfilled.',
        ],
      },
    ],
    relatedSections: ['publications', 'pricebooks', 'orders'],
  },
  markets: {
    sectionId: 'markets',
    title: 'Markets',
    summary:
      'Markets are tenant-owned geographic rollout objects. They describe where commerce can sell and how rollout readiness is grouped for eligibility decisions.',
    metrics: [
      { label: 'Object', value: 'Geographic rollout' },
      { label: 'Controls', value: 'Sign-up and install readiness' },
      { label: 'Used By', value: 'Eligibility and storefront gating' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What This Is',
        paragraphs: [
          'Serviceability zones hold the geography and lookup bindings that market resolution depends on. Operators should define zones first, then attach markets to those zones.',
          'A market represents a serviceable or planned geography owned by the catalog owner tenant. It is where rollout state, install posture, and customer-facing rollout messaging live.',
          'Market segments group markets into reusable clusters so eligibility policies can target a region or rollout cohort without editing every market one by one.',
        ],
      },
      {
        id: 'why',
        title: 'Why You Use It',
        paragraphs: [
          'Use markets when serviceability differs by geography or rollout stage. They let you separate product visibility from actual readiness to sell or install.',
          'Segments are useful when you repeatedly target the same set of markets across policies, promotions, or operational review.',
        ],
      },
      {
        id: 'how',
        title: 'How It Works',
        paragraphs: [
          'Markets are not the publication layer. They do not decide who can sell a catalog. They decide where commerce context says the offer is valid or ready.',
          'Zone metadata can temporarily carry bindings such as polygons, bounds, circles, and place identifiers until native GIS-backed authoring is implemented.',
          'Eligibility policies reference markets or segments. Orders and storefront flows then resolve those rules at runtime for the selected selling context and geography.',
        ],
      },
      {
        id: 'mistakes',
        title: 'Common Mistakes',
        paragraphs: [
          'Do not use markets as reseller targeting. Reseller targeting belongs in publications, assignments, and entitlements.',
          'Do not overload market metadata with product logic. Markets should stay focused on rollout and geography.',
        ],
      },
    ],
    relatedSections: ['eligibility', 'publications', 'orders'],
  },
  eligibility: {
    sectionId: 'eligibility',
    title: 'Eligibility',
    summary:
      'Eligibility policies are the runtime serviceability rules. They decide whether a published offer can actually be sold in a given market and selling context.',
    metrics: [
      { label: 'Object', value: 'Decision rules' },
      { label: 'Controls', value: 'Sellability by context' },
      { label: 'Used By', value: 'Catalog read, cart, quote, order' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What This Is',
        paragraphs: [
          'Eligibility policies are the rule records that convert geography, market readiness, selling scope, and publication context into an allow or deny decision.',
          'They are where you express conditions like whether sign-up is allowed, whether scheduling is allowed, and which subject the rule applies to.',
        ],
      },
      {
        id: 'when',
        title: 'When To Use It',
        paragraphs: [
          'Use eligibility when an offer is published but should only be valid for some markets, segments, stores, agents, or selling tenants.',
          'Do not use eligibility to express customer pricing or commissions. It answers can sell, not how much or who gets paid.',
        ],
      },
      {
        id: 'how',
        title: 'How It Works',
        paragraphs: [
          'Eligibility sits after publication visibility. A user might be able to browse a catalog and still fail eligibility for a concrete service address or market context.',
          'Policies should be authored so operators can explain the outcome. Priority, status, validity windows, and requirement flags should tell a coherent story.',
        ],
      },
      {
        id: 'mistakes',
        title: 'Common Mistakes',
        paragraphs: [
          'Do not create overlapping policies that say different things for the same scope unless you understand the precedence model.',
          'Do not treat eligibility as a replacement for geographic data hygiene. Markets and segments still need to be curated accurately.',
        ],
      },
    ],
    relatedSections: ['markets', 'publications', 'orders'],
  },
  pricebooks: {
    sectionId: 'pricebooks',
    title: 'Pricebooks',
    summary:
      'Pricebooks hold the customer-facing commercial amounts. They are how the owner tenant expresses pricing by selling context without forking the catalog.',
    metrics: [
      { label: 'Object', value: 'Commercial pricing' },
      { label: 'Controls', value: 'Amounts, overrides, readiness' },
      { label: 'Used By', value: 'Publications, assignments, checkout' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What This Is',
        paragraphs: [
          'A pricebook is the pricing layer for canonical catalog variants and fees. It answers what the buyer should be charged for a given selling context.',
          'Pricebooks remain owner-controlled even when they are scoped to a reseller or another selling tenant.',
        ],
      },
      {
        id: 'when',
        title: 'When To Use It',
        paragraphs: [
          'Use pricebooks when the same catalog needs different commercial amounts by selling context, rollout, or promotion strategy.',
          'Do not create new products just to model a pricing change. That usually means you need a pricebook or a pricebook assignment.',
        ],
      },
      {
        id: 'how',
        title: 'How It Works',
        paragraphs: [
          'A publication can name a default pricebook. Assignments can then override that default for a more specific target such as a selling tenant, store, or agent.',
          'Quotes and orders snapshot the resolved pricebook so later reporting stays stable even if pricing is changed after the sale.',
        ],
      },
      {
        id: 'mistakes',
        title: 'Common Mistakes',
        paragraphs: [
          'Do not leave production pricebooks half-covered. Partial pricing makes storefront and quote behavior harder to reason about.',
          'Do not put fee-definition logic into pricebooks. Pricebooks price fees; they do not define what a fee is.',
        ],
      },
    ],
    relatedSections: ['assignments', 'publications', 'catalogs'],
  },
  assignments: {
    sectionId: 'assignments',
    title: 'Assignments',
    summary:
      'Assignments are the precedence layer for pricing and commission overrides. They target a more specific selling context than the publication default.',
    metrics: [
      { label: 'Object', value: 'Override targeting' },
      { label: 'Controls', value: 'Specific commercial selection' },
      { label: 'Used By', value: 'Pricebook and commission resolution' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What This Is',
        paragraphs: [
          'Assignments attach a pricebook or commission plan to a narrower target such as a publication, organization, selling tenant, store, or agent.',
          'They exist so you can keep the publication stable while layering on more specific commercial behavior where needed.',
        ],
      },
      {
        id: 'why',
        title: 'Why You Use It',
        paragraphs: [
          'Use assignments when one reseller, store, or rep should resolve to a different commercial term set than the publication default.',
          'They are especially useful when you need narrow overrides without multiplying publications.',
        ],
      },
      {
        id: 'how',
        title: 'How It Works',
        paragraphs: [
          'Assignments participate in deterministic resolution. The most specific valid target should win, with publication defaults filling in when no override applies.',
          'Because they are override records, assignments should stay sparse and intentional. They work best when the default path is already coherent.',
        ],
      },
      {
        id: 'mistakes',
        title: 'Common Mistakes',
        paragraphs: [
          'Do not create many overlapping assignments unless the precedence model is obvious to operators.',
          'Do not use assignments to paper over a bad default publication setup. Fix the default if most targets need the same override.',
        ],
      },
    ],
    relatedSections: ['pricebooks', 'commission_plans', 'publications'],
  },
  publications: {
    sectionId: 'publications',
    title: 'Publications',
    summary:
      'Publications are the exposure layer. They decide where a catalog is visible and which default commercial terms should be used when nothing more specific applies.',
    metrics: [
      { label: 'Object', value: 'Exposure and defaults' },
      { label: 'Controls', value: 'Audience and baseline terms' },
      { label: 'Used By', value: 'Catalog access and resolution' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What This Is',
        paragraphs: [
          'A publication is the record that exposes a catalog to a storefront or selling audience. It is where scope, status, default pricebook, and default commission plan are set.',
          'Think of publications as the bridge between the owner catalog and the selling world.',
        ],
      },
      {
        id: 'when',
        title: 'When To Use It',
        paragraphs: [
          'Use publications when deciding who can see and use a catalog at all. This is the correct place for public exposure versus private reseller exposure.',
          'Do not use publications for narrow per-store overrides. That is what assignments are for.',
        ],
      },
      {
        id: 'how',
        title: 'How It Works',
        paragraphs: [
          'A publication establishes the baseline commercial context: catalog, audience scope, default pricebook, and default commission plan.',
          'Eligibility, assignments, and entitlements then refine what that publication means for a particular selling context or geography.',
        ],
      },
      {
        id: 'mistakes',
        title: 'Common Mistakes',
        paragraphs: [
          'Do not publish many near-identical records when one publication plus assignments would be easier to operate.',
          'Do not treat a publication as proof that an offer is sellable everywhere. Eligibility still applies.',
        ],
      },
    ],
    relatedSections: ['pricebooks', 'commission_plans', 'eligibility'],
  },
  channels: {
    sectionId: 'channels',
    title: 'Channels',
    summary:
      'Channels are the storefront authorization layer. They bind one approved publication to one approved caller set so public commerce does not drift onto the wrong tenant or storefront.',
    metrics: [
      { label: 'Object', value: 'Storefront binding' },
      { label: 'Controls', value: 'Allowed callers and publication' },
      { label: 'Used By', value: 'Public signup and checkout' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What This Is',
        paragraphs: [
          'A channel is the runtime record that says which storefront or browser-facing flow is allowed to serve a specific publication.',
          'It exists so a public storefront does not resolve against whichever matching publication happened to be created most recently.',
        ],
      },
      {
        id: 'when',
        title: 'When To Use It',
        paragraphs: [
          'Use channels when a tenant-owned storefront, embedded checkout, or first-party signup experience must be pinned to one publication and one allowed caller set.',
          'Do not use channels to manage audience sharing. Audience scope still belongs to the publication itself.',
        ],
      },
      {
        id: 'how',
        title: 'How It Works',
        paragraphs: [
          'A channel has a stable key, allowed principals, one default publication, and optional conditional publication routes. Runtime selects one publication by direct market, market group, trusted audience, priority, then the default fallback.',
          'If the allowed principals or binding do not line up, the public storefront should fail closed instead of falling through to another tenant publication.',
        ],
      },
      {
        id: 'mistakes',
        title: 'Common Mistakes',
        paragraphs: [
          'Do not assume a storefront key by itself is enough. The safe binding is the channel plus its saved publication.',
          'Do not leave multiple active primary bindings on the same channel. That makes storefront behavior ambiguous.',
        ],
      },
    ],
    relatedSections: ['publications', 'reseller_entitlements', 'orders'],
  },
  reseller_entitlements: {
    sectionId: 'reseller_entitlements',
    title: 'Entitlements',
    summary:
      'Entitlements are the seller-side source of truth for who may resell a publication and which default commission plan applies to that reseller context.',
    metrics: [
      { label: 'Object', value: 'Authorization + default commission' },
      { label: 'Controls', value: 'Operator authoring of reseller access' },
      { label: 'Used By', value: 'Catalog-owner administration and reseller review' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What This Is',
        paragraphs: [
          'Entitlements define which tenants, stores, and agents may sell a publication.',
          'They also carry the reseller default commission plan that applies when no more specific commission assignment overrides the seller context.',
        ],
      },
      {
        id: 'why',
        title: 'Why You Use It',
        paragraphs: [
          'Use entitlements when you need to confirm whether a selling tenant or reseller workspace should currently have access.',
          'They are also the right place to set the reseller default commission plan instead of relying on publication-level commission defaults.',
        ],
      },
      {
        id: 'how',
        title: 'How It Works',
        paragraphs: [
          'Each entitlement references one publication, one seller audience, and an optional default commission plan.',
          'Assignment rows still win for narrower publication, tenant, store, and agent overrides, but the entitlement owns the reseller baseline.',
        ],
      },
      {
        id: 'mistakes',
        title: 'Common Mistakes',
        paragraphs: [
          'Do not leave reseller commission defaults only on the publication. Use the entitlement when the default should follow the entitled seller.',
          'Do not confuse owner-side visibility with reseller-side resolved access. Both views matter, but they answer different questions.',
        ],
      },
    ],
    relatedSections: ['publications', 'assignments', 'orders'],
  },
  commission_plans: {
    sectionId: 'commission_plans',
    title: 'Commission Plans',
    summary:
      'Commission plans define payout policy snapshots for reseller and agent selling. They are the payout analogue to pricebooks.',
    metrics: [
      { label: 'Object', value: 'Payout policy' },
      { label: 'Controls', value: 'How sellers get paid' },
      { label: 'Used By', value: 'Publications, assignments, commission ledger' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What This Is',
        paragraphs: [
          'A commission plan is the owner-controlled payout policy used when commerce attributes reseller or agent credit for a sale.',
          'It belongs to the catalog owner tenant even when it targets a specific selling tenant.',
        ],
      },
      {
        id: 'when',
        title: 'When To Use It',
        paragraphs: [
          'Use commission plans whenever payout policy changes by partner, reseller program, or operational model.',
          'Do not mix payout policy into pricebooks. Pricebooks are buyer-facing; commission plans are seller-facing.',
        ],
      },
      {
        id: 'how',
        title: 'How It Works',
        paragraphs: [
          'A publication may define a default commission plan, and assignments can override that for more specific targets.',
          'Orders snapshot the resolved commission plan context so later payout reporting remains interpretable even after plan changes.',
        ],
      },
      {
        id: 'mistakes',
        title: 'Common Mistakes',
        paragraphs: [
          'Do not leave plan intent implicit in raw JSON alone. Operators need to understand whether a plan is percentage-based, flat, or hybrid.',
          'Do not treat the commission plan as a ledger entry. The plan defines policy; the ledger records what happened.',
        ],
      },
    ],
    relatedSections: ['assignments', 'commission_ledger', 'publications'],
  },
  commission_ledger: {
    sectionId: 'commission_ledger',
    title: 'Commission Ledger',
    summary:
      'The commission ledger is the durable accounting record for commerce payouts. It shows what commission entries were created and where they are in the payout lifecycle.',
    metrics: [
      { label: 'Object', value: 'Accounting record' },
      { label: 'Controls', value: 'Audit and payout review' },
      { label: 'Used By', value: 'Finance and reseller reporting' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What This Is',
        paragraphs: [
          'The commission ledger is the historical record of earned, approved, paid, or reversed commission activity.',
          'It is not where payout policy is authored. It is where policy outcomes are audited.',
        ],
      },
      {
        id: 'when',
        title: 'When To Use It',
        paragraphs: [
          'Use the ledger when reconciling partner earnings, investigating a disputed payout, or verifying what happened after an order was submitted.',
          'This is a reporting and review surface, not a primary configuration surface.',
        ],
      },
      {
        id: 'how',
        title: 'How It Works',
        paragraphs: [
          'Ledger entries are downstream artifacts of commerce events. They should point back to the resolved commission plan and the selling context that produced them.',
          'Because entries are durable records, the ledger is the place to answer what happened, not what should happen next time.',
        ],
      },
      {
        id: 'mistakes',
        title: 'Common Mistakes',
        paragraphs: [
          'Do not try to fix future payout behavior by editing the ledger. Adjust the commission plan or assignments instead.',
          'Do not assume a missing plan label means no commission logic existed. Check the snapshotted ids and order context.',
        ],
      },
    ],
    relatedSections: ['commission_plans', 'orders', 'reseller_entitlements'],
  },
  discounts: {
    sectionId: 'discounts',
    title: 'Discounts',
    summary:
      'Discounts and promotions are temporary or targeted commercial incentives. They change the commercial offer without changing the underlying catalog definition.',
    metrics: [
      { label: 'Object', value: 'Promotion campaigns' },
      { label: 'Controls', value: 'Incentives and codes' },
      { label: 'Used By', value: 'Catalog read, cart, checkout' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What This Is',
        paragraphs: [
          'Discounts model promotion campaigns, benefits, targets, and optional redemption codes.',
          'They are how commerce expresses temporary incentives such as a percent off, a fixed amount off, or code-gated offers.',
        ],
      },
      {
        id: 'when',
        title: 'When To Use It',
        paragraphs: [
          'Use discounts when pricing should change because of a campaign or incentive, not because the baseline product price changed.',
          'If the new amount should be the everyday default for a selling context, use a pricebook instead.',
        ],
      },
      {
        id: 'how',
        title: 'How It Works',
        paragraphs: [
          'Promotions evaluate against context and targets, then contribute a discount result into cart or checkout flows.',
          'Because discounts sit on top of pricing, they should be explainable in relation to the underlying pricebook and product scope.',
        ],
      },
      {
        id: 'mistakes',
        title: 'Common Mistakes',
        paragraphs: [
          'Do not use discounts to replace pricebook governance for long-lived pricing changes.',
          'Do not create overlapping campaigns without understanding stacking policy and precedence.',
        ],
      },
    ],
    relatedSections: ['pricebooks', 'carts', 'orders'],
  },
  carts: {
    sectionId: 'carts',
    title: 'Carts',
    summary:
      'Carts are the active commercial session. They capture the in-progress sell configuration before it becomes a quote or order.',
    metrics: [
      { label: 'Object', value: 'Active session' },
      { label: 'Controls', value: 'In-flight commercial state' },
      { label: 'Used By', value: 'Checkout and order creation' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What This Is',
        paragraphs: [
          'A cart is the working commercial state for a buyer or seller session. It holds selected items, pricing context, discounts, and other derived commercial data before submission.',
          'Carts are transient compared with orders, but they are still important for debugging runtime resolution.',
        ],
      },
      {
        id: 'when',
        title: 'When To Use It',
        paragraphs: [
          'Use carts when you need to inspect how commerce is resolving terms before an order is finalized.',
          'This is the right place to debug active pricing, discounts, and context-specific selection issues.',
        ],
      },
      {
        id: 'how',
        title: 'How It Works',
        paragraphs: [
          'Carts accumulate the resolved commercial context from publications, eligibility, pricebooks, and promotions.',
          'They are an intermediate state, so they can reveal a problem before it becomes a durable quote or order.',
        ],
      },
      {
        id: 'mistakes',
        title: 'Common Mistakes',
        paragraphs: [
          'Do not use carts as the long-term source of truth for historical reporting. That is what orders and ledger records are for.',
          'Do not assume a cart issue is isolated to the cart. It often points back to pricing, publication, or eligibility configuration.',
        ],
      },
    ],
    relatedSections: ['discounts', 'quotes', 'orders'],
  },
  quotes: {
    sectionId: 'quotes',
    title: 'Quotes',
    summary:
      'Quotes are the commercial offer prepared for a buyer. They turn active configuration into a reviewable proposal before an order is placed.',
    metrics: [
      { label: 'Object', value: 'Commercial proposal' },
      { label: 'Controls', value: 'Pre-order commitment' },
      { label: 'Used By', value: 'Sales workflow and conversion' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What This Is',
        paragraphs: [
          'A quote captures the proposed commercial terms for a buyer before order submission.',
          'It snapshots the resolved context clearly enough that a seller and customer can review the offer as proposed at that moment in time.',
        ],
      },
      {
        id: 'when',
        title: 'When To Use It',
        paragraphs: [
          'Use quotes when a sales workflow needs an intermediate approval or negotiation step before an order is finalized.',
          'If the experience is immediate self-service checkout, a cart may move directly to order without a long-lived quote step.',
        ],
      },
      {
        id: 'how',
        title: 'How It Works',
        paragraphs: [
          'Quotes should snapshot resolved pricebook, publication, and commission context so later review remains understandable.',
          'They are part of the selling workflow, so the right audience usually cares about both buyer-facing terms and seller attribution context.',
        ],
      },
      {
        id: 'mistakes',
        title: 'Common Mistakes',
        paragraphs: [
          'Do not treat quotes as a replacement for order history. Quotes represent proposed terms, not completed fulfillment.',
          'Do not rely on live config lookups alone when reading an old quote. Snapshotted context matters.',
        ],
      },
    ],
    relatedSections: ['carts', 'orders', 'commission_plans'],
  },
  orders: {
    sectionId: 'orders',
    title: 'Orders',
    summary:
      'Orders are the durable commercial outcome. They snapshot what was sold, under which commercial terms, and with which selling attribution.',
    metrics: [
      { label: 'Object', value: 'Durable transaction' },
      { label: 'Controls', value: 'Fulfillment and reporting' },
      { label: 'Used By', value: 'Operations, finance, commissions' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What This Is',
        paragraphs: [
          'An order is the durable record of a completed commercial commitment. It should preserve catalog owner, selling tenant, publication, pricing, and commission context as submitted.',
          'Orders are where commercial intent becomes operational work and downstream accounting.',
        ],
      },
      {
        id: 'when',
        title: 'When To Use It',
        paragraphs: [
          'Use orders when reviewing what actually happened in commerce: what was sold, who sold it, and what downstream workflows should follow.',
          'This is the right surface for operational and reporting review, not for authoring commercial policy.',
        ],
      },
      {
        id: 'how',
        title: 'How It Works',
        paragraphs: [
          'Orders snapshot the resolved commercial context at submission time so later config changes do not rewrite history.',
          'That includes publication, pricebook, commission plan, and related selling attribution.',
        ],
      },
      {
        id: 'mistakes',
        title: 'Common Mistakes',
        paragraphs: [
          'Do not debug an order by only looking at current config. The snapshotted order context is the primary truth for what the system decided at the time.',
          'Do not mix owner-side and reseller-side reporting questions. Orders support both, but they are different views over the same durable record.',
        ],
      },
    ],
    relatedSections: ['quotes', 'commission_ledger', 'reseller_entitlements'],
  },
};

export function getCommerceHelpArticle(sectionId: string): CommerceHelpArticle {
  return articles[(sectionId as CommerceAdminSectionId) || 'catalogs'] || articles.catalogs;
}

export function getCommerceHelpArticles(): CommerceHelpArticle[] {
  return Object.values(articles);
}
